const listeners = new Set();

export function subscribeNotices(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function showNotice(notice) {
  const next = {
    tone: 'info',
    title: 'Something went wrong',
    message: '',
    details: [],
    confirmLabel: 'OK',
    ...notice,
  };
  listeners.forEach((listener) => listener(next));
}

function formatWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatWait(ms) {
  const total = Math.max(0, Math.floor(Number(ms) / 1000));
  if (!Number.isFinite(total)) return '';
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return 'less than a minute';
}

function detailRows(rows) {
  return rows.filter((row) => row && row.value);
}

export function showMeetingBlocked(info = {}) {
  const reason = info.reason;
  const start = formatWhen(info.scheduledStartTime);
  const end = formatWhen(info.scheduledEndTime);
  const meeting = info.title ? { label: 'Meeting', value: info.title } : null;

  if (reason === 'upcoming') {
    showNotice({
      tone: 'info',
      icon: 'schedule',
      title: "This meeting hasn't started",
      message: 'You can join once the meeting opens.',
      details: detailRows([
        meeting,
        { label: 'Starts', value: start },
        { label: 'Ends', value: end },
        { label: 'Opens in', value: formatWait(info.timeUntilStart) },
      ]),
    });
    return;
  }

  if (reason === 'ended') {
    showNotice({
      tone: 'info',
      icon: 'schedule',
      title: 'This meeting has ended',
      message: 'The scheduled time for this meeting is over.',
      details: detailRows([
        meeting,
        { label: 'Started', value: start },
        { label: 'Ended', value: end },
      ]),
    });
    return;
  }

  showNotice({
    tone: 'error',
    title: "Can't join this meeting",
    message: info.message || info.error || 'This meeting is not available right now.',
    details: detailRows([
      meeting,
      start && { label: 'Starts', value: start },
      end && { label: 'Ends', value: end },
    ]),
  });
}

function noticeFromMessage(message) {
  const text = String(message || '').trim();
  const cleaned = text.replace(/^Error joining room:\s*/i, '');

  if (/not available yet/i.test(cleaned)) {
    const starts = cleaned.match(/Starts at\s+(.+?)(?:\s+\(|$)/i);
    const wait = cleaned.match(/\((?:in\s+)?([^)]+)\)\s*$/i);
    return {
      tone: 'info',
      icon: 'schedule',
      title: "This meeting hasn't started",
      message: 'You can join once the meeting opens.',
      details: detailRows([
        { label: 'Starts', value: starts?.[1]?.trim() },
        { label: 'Opens in', value: wait?.[1]?.replace(/^in\s+/i, '').trim() },
      ]),
    };
  }

  if (/has ended/i.test(cleaned)) {
    const until = cleaned.match(/until\s+(.+)$/i);
    return {
      tone: 'info',
      icon: 'schedule',
      title: 'This meeting has ended',
      message: 'The scheduled time for this meeting is over.',
      details: detailRows([{ label: 'Ended', value: until?.[1]?.trim() }]),
    };
  }

  if (/code or link/i.test(cleaned) && /name/i.test(cleaned)) {
    return {
      tone: 'info',
      title: 'Details required',
      message: 'Enter your name and a meeting code or MeetGeek link.',
    };
  }

  if (/your name/i.test(cleaned)) {
    return {
      tone: 'info',
      title: 'Name required',
      message: 'Enter your name before joining or starting a meeting.',
    };
  }

  if (/invalid room link/i.test(cleaned)) {
    return {
      title: 'Invalid meeting link',
      message: 'This link does not point to a meeting. Check it and try again.',
    };
  }

  if (/connection lost/i.test(cleaned)) {
    return {
      title: 'Connection lost',
      message: 'The meeting connection dropped. Refresh the page to rejoin.',
    };
  }

  if (/failed to create meeting/i.test(cleaned)) {
    return {
      title: "Couldn't start the meeting",
      message: cleaned,
    };
  }

  if (/sage/i.test(cleaned)) {
    return {
      title: "Sage couldn't join",
      message: cleaned,
    };
  }

  if (/failed to (get token|join)/i.test(cleaned)) {
    return {
      title: "Couldn't join the meeting",
      message: cleaned,
    };
  }

  return {
    tone: 'error',
    title: 'Something went wrong',
    message: cleaned || 'Please try again.',
  };
}

export function showError(messageOrNotice) {
  if (messageOrNotice && typeof messageOrNotice === 'object') {
    showNotice({ tone: 'error', ...messageOrNotice });
    return;
  }
  showNotice({ tone: 'error', ...noticeFromMessage(messageOrNotice) });
}

export function showSuccess(messageOrNotice) {
  if (messageOrNotice && typeof messageOrNotice === 'object') {
    showNotice({ tone: 'success', title: 'Done', ...messageOrNotice });
    return;
  }
  showNotice({
    tone: 'success',
    title: 'Done',
    message: String(messageOrNotice || '').trim(),
  });
}
